/**
 * LandingPage – entry screen with topic cards (Living Arrangements, Funding, Impacts, Next Steps).
 * Used by App.js when activePage === 'LandingPage'.
 */
import React, { useState, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Home, DollarSign, HeartPulse, NotebookText, ShieldCheck, Users, Bot, ArrowRight } from 'lucide-react';
import { AgedCareWiseLogo, agedCareWiseMarkUrl } from '../components/branding/index.tsx';
import AnimatedBackground from '../components/layout/AnimatedBackground.tsx';
import { GradientText } from '../components/ui/index.tsx';
import AgedCareWiseAiChatPanel from '../components/chat/AgedCareWiseAiChatPanel.tsx';
import { sendMessageToGemini } from '../services/geminiChat.ts';
import type { GeminiChatTurn } from '../services/geminiChat.ts';
import {
    LANDING_PAGE_AI_SYSTEM_INSTRUCTION,
    LANDING_PAGE_AI_WELCOME,
} from '../constants/agedCareWiseAiPrompts.ts';

const LIVING_PATHWAY_PILLS = [
    'Support at home',
    'Downsizing',
    'Retirement village',
    'Granny flat',
    'Aged care',
];

type ChatMessage = GeminiChatTurn;

interface TopicCardProps {
    icon: LucideIcon;
    title: string;
    text: string;
    onClick: () => void;
}

const TopicCard = ({ icon: Icon, title, text, onClick }: TopicCardProps) => (
    <div onClick={onClick} className="group relative bg-white/80 backdrop-blur-xl p-6 rounded-2xl text-left transition-all duration-300 cursor-pointer border border-gray-200/50 overflow-hidden hover:shadow-2xl hover:shadow-blue-200/50 hover:-translate-y-2 hover:border-blue-300/50">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-transparent to-teal-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative z-10">
            <div className="w-14 h-14 mb-4 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-teal-400 text-white shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                <Icon size={32} className="group-hover:scale-110 transition-transform duration-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors duration-300">{title}</h3>
            <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{text}</p>
        </div>
        <ArrowRight size={24} className="absolute bottom-6 right-6 text-gray-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 translate-x-4 transition-all duration-300 group-hover:text-blue-500" />
    </div>
);

export interface LandingPageProps {
    setActivePage?: (page: string) => void;
    setShowGeneralAdvice: (section: string) => void;
    setShowRegistration: (show: boolean) => void;
    onProfessionalLogin?: () => void;
    onClientLogin?: () => void;
    onOpenSecurity?: () => void;
    onOpenPrivacy?: () => void;
    onOpenCost?: () => void;
    onOpenExperience?: () => void;
    onOpenFaqs?: () => void;
}

const LandingPage = ({ setShowGeneralAdvice, setShowRegistration, onProfessionalLogin, onClientLogin, onOpenSecurity, onOpenPrivacy, onOpenCost, onOpenExperience, onOpenFaqs }: LandingPageProps) => {
    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([{ sender: 'bot', text: LANDING_PAGE_AI_WELCOME }]);
    const [chatInput, setChatInput] = useState('');
    const [chatSending, setChatSending] = useState(false);

    const topics = [
        { icon: Home, title: "Living Arrangements", text: "Explore support at home, downsizing, retirement villages, granny flats, or aged care facilities.", section: "living" },
        { icon: DollarSign, title: "Funding Options", text: "Understand costs: keep/sell your home, RAD vs DAP, or government subsidies.", section: "funding" },
        { icon: HeartPulse, title: "Care & Financial Impacts", text: "See how your choices affect cash flow, pensions, estate planning & risk.", section: "impacts" },
        { icon: NotebookText, title: "Next Steps", text: "Get actionable tasks: paperwork, expert contacts, and timelines.", section: "nextSteps" },
    ];

    const submitChatMessage = useCallback(async () => {
        const trimmed = chatInput.trim();
        if (!trimmed || chatSending) return;
        setChatOpen(true);
        setChatSending(true);
        const history = chatMessages;
        setChatMessages((msgs) => [...msgs, { sender: 'user', text: trimmed }]);
        setChatInput('');
        try {
            const aiReply = await sendMessageToGemini({
                message: trimmed,
                systemInstruction: LANDING_PAGE_AI_SYSTEM_INSTRUCTION,
                conversationHistory: history,
            });
            setChatMessages((msgs) => [...msgs, { sender: 'bot', text: aiReply }]);
        } catch (e) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[AgedCareWise AI] landing send failed', e);
            }
            setChatMessages((msgs) => [
                ...msgs,
                {
                    sender: 'bot',
                    text: 'Something went wrong sending your message. You can try again in a moment.',
                },
            ]);
        } finally {
            setChatSending(false);
        }
    }, [chatInput, chatSending, chatMessages]);

    const openChat = () => setChatOpen(true);

    return (
        <div className="relative min-h-screen w-full bg-[#F0F7FF] font-sans text-gray-800 overflow-x-hidden">
            <div className="absolute inset-0 -z-10">
                <div className="absolute top-0 left-0 w-full h-[60vh] bg-gradient-to-b from-blue-100 via-blue-50 to-transparent"></div>
            </div>

            <header className="absolute top-0 left-0 right-0 z-10 px-4 sm:px-6 py-4 md:py-5 max-w-screen-2xl mx-auto w-full">
                <div className="flex flex-col gap-3 md:gap-3.5">
                <div className="flex items-center justify-between gap-3 min-w-0">
                <div className="shrink-0 min-w-0 flex items-center gap-0.5 sm:gap-1">
                    <img
                        src={agedCareWiseMarkUrl}
                        alt=""
                        width={80}
                        height={80}
                        className="h-[3.35rem] w-[3.35rem] sm:h-[4.45rem] sm:w-[4.45rem] md:h-[5.55rem] md:w-[5.55rem] shrink-0 object-contain object-left select-none -mr-2.5 sm:-mr-3 md:-mr-3.5"
                        decoding="async"
                        draggable={false}
                    />
                    <AgedCareWiseLogo className="h-12 sm:h-16 md:h-20 w-auto min-w-0" />
                </div>
                <div className="shrink-0 flex flex-col gap-2 w-full max-w-[15rem] sm:max-w-[16rem] md:max-w-[18rem]">
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="flex-1 min-w-0 text-center text-[11px] sm:text-xs font-semibold rounded-full border-2 border-blue-600/70 text-blue-700 bg-white/20 backdrop-blur-md hover:bg-white/35 hover:border-blue-600 py-2 px-1.5 sm:px-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:ring-offset-2 leading-tight shadow-sm shadow-blue-900/10"
                            onClick={() => {
                                if (onClientLogin) onClientLogin();
                            }}
                        >
                            Client Login
                        </button>
                        <button
                            type="button"
                            className="flex-1 min-w-0 text-center text-[11px] sm:text-xs font-semibold rounded-full border-2 border-blue-600/70 text-blue-700 bg-white/20 backdrop-blur-md hover:bg-white/35 hover:border-blue-600 py-2 px-1.5 sm:px-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:ring-offset-2 leading-tight shadow-sm shadow-blue-900/10"
                            onClick={() => {
                                if (onProfessionalLogin) onProfessionalLogin();
                            }}
                        >
                            Partner Portal
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setShowRegistration(true);
                        }}
                        className="w-full bg-blue-600 text-white font-semibold py-2.5 rounded-full text-sm shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Get Started
                    </button>
                </div>
                </div>
                <nav
                    className="hidden md:flex items-center justify-center gap-x-4 lg:gap-x-6 xl:gap-x-8 flex-wrap text-gray-600 font-medium text-xs lg:text-sm leading-normal"
                    aria-label="Site sections"
                >
                    <button
                        type="button"
                        onClick={() => onOpenExperience && onOpenExperience()}
                        className="whitespace-nowrap hover:text-blue-600 transition-colors duration-300"
                    >
                        The Experience
                    </button>
                    <button
                        type="button"
                        onClick={() => onOpenCost && onOpenCost()}
                        className="whitespace-nowrap hover:text-blue-600 transition-colors duration-300"
                    >
                        The Cost
                    </button>
                    <button
                        type="button"
                        onClick={() => onOpenPrivacy && onOpenPrivacy()}
                        className="whitespace-nowrap hover:text-blue-600 transition-colors duration-300"
                    >
                        Privacy
                    </button>
                    <button
                        type="button"
                        onClick={() => onOpenSecurity && onOpenSecurity()}
                        className="whitespace-nowrap hover:text-blue-600 transition-colors duration-300"
                    >
                        Security
                    </button>
                    <button
                        type="button"
                        onClick={() => onOpenFaqs && onOpenFaqs()}
                        className="whitespace-nowrap hover:text-blue-600 transition-colors duration-300"
                    >
                        FAQs
                    </button>
                    <a href="#" className="whitespace-nowrap hover:text-blue-600 transition-colors duration-300">About Us</a>
                </nav>
                </div>
            </header>

            <AnimatedBackground />

            <main className="relative pt-36 md:pt-48 pb-24 flex flex-col items-center text-center px-4">
                <div className="bg-blue-100 text-blue-700 font-semibold px-4 py-1.5 rounded-full mb-4 text-sm">
                    Your Personal Aged Care Adviser
                </div>
                <h1 className="text-4xl md:text-6xl font-bold text-gray-900 max-w-4xl leading-tight">
                    Navigate your <GradientText>Retirement</GradientText> &amp; <GradientText>Aged Care</GradientText> Living Decisions
                </h1>
                <p className="mt-5 max-w-2xl text-base sm:text-lg text-gray-600 leading-relaxed">
                    Explore how different living arrangements impact your cashflow, Age Pension, and what&apos;s left to your estate.
                    {' '}Let us work through strategies to improve your outcomes.
                </p>

                <div
                    className="mt-6 flex flex-wrap items-center justify-center gap-2 max-w-3xl"
                    role="list"
                    aria-label="Living pathways"
                >
                    {LIVING_PATHWAY_PILLS.map((label) => (
                        <span
                            key={label}
                            role="listitem"
                            className="px-3 py-1.5 rounded-full bg-white/80 border border-blue-200 text-sm font-medium text-slate-700 backdrop-blur-sm"
                        >
                            {label}
                        </span>
                    ))}
                </div>

                <div className="mt-8 flex flex-col md:flex-row items-center gap-6">
                    <div className="bg-white/70 backdrop-blur-sm p-3 rounded-full shadow-md flex items-center gap-3 hover:bg-white/80 hover:shadow-lg transition-all duration-300">
                        <ShieldCheck className="text-blue-500" />
                        <span className="font-semibold">Data is Secure</span>
                    </div>
                     <div className="bg-white/70 backdrop-blur-sm p-3 rounded-full shadow-md flex items-center gap-3 hover:bg-white/80 hover:shadow-lg transition-all duration-300">
                        <Users className="text-teal-500" />
                        <span className="font-semibold">Built by Advisers</span>
                    </div>
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 w-full max-w-screen-xl">
                    {topics.map((topic, i) => (
                        <TopicCard key={i} {...topic} onClick={() => {
                            setShowGeneralAdvice(topic.section);
                        }} />
                    ))}
                </div>
            </main>

            <footer className="sticky bottom-6 w-full max-w-3xl mx-auto px-4 z-30">
                <div className="bg-white/80 backdrop-blur-lg rounded-xl shadow-2xl shadow-blue-200/50 p-3 flex items-center gap-3 hover:shadow-3xl hover:shadow-blue-300/50 transition-all duration-300">
                    <button
                        type="button"
                        onClick={openChat}
                        className="shrink-0 text-blue-600 hover:text-blue-800 p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        aria-label="Open AWise chat"
                    >
                        <Bot className="h-8 w-8" />
                    </button>
                    <input
                        type="text"
                        placeholder="What's on your mind?"
                        className="w-full bg-transparent focus:outline-none text-gray-700 placeholder-gray-500 text-sm sm:text-base"
                        aria-label="Ask AWise"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onFocus={openChat}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter' || e.shiftKey) return;
                            e.preventDefault();
                            submitChatMessage();
                        }}
                        disabled={chatSending}
                    />
                    <button
                        type="button"
                        className="bg-blue-600 p-2 rounded-lg text-white hover:bg-blue-700 hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 shrink-0"
                        onClick={submitChatMessage}
                        disabled={chatSending || !chatInput.trim()}
                        aria-label="Send message"
                    >
                        <ArrowRight size={20} />
                    </button>
                </div>
            </footer>

            <AgedCareWiseAiChatPanel
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                messages={chatMessages}
                chatInput={chatInput}
                onChatInputChange={setChatInput}
                onSubmit={submitChatMessage}
                sending={chatSending}
                onSubscribe={() => setShowRegistration(true)}
                subscribeLabel="Get Started"
            />
        </div>
    );
};

export default LandingPage;
